import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Card,
  CardBody,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Flex,
  Icon,
  HStack,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
} from '@chakra-ui/react';
import { FaInfoCircle, FaExclamationTriangle, FaSignOutAlt, FaSun, FaMoon } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useAppInfoContext } from '../../contexts/AppInfoContext';
import { ConfigService } from '../../services/configService';
import { CollectionService } from '../../services/collectionService';
import { InputArea, InputAreaRef } from './InputArea';
import { ResponseViewer, ResponseViewerRef } from './ResponseViewer';
import { useTheme } from '../../hooks/useTheme';
import { Config, Collection } from '../../types/config';
import { GitLabFileBrowser } from './GitLabFileBrowser';
import { AssistantChatClientRefactored, AssistantChatClientRef } from './AssistantChatClientRefactored';
import { DocumentSelector } from '../layout/DocumentSelector';

export const SharedDocument: React.FC = () => {
  const { shareId: paramShareId } = useParams<{ shareId: string }>();
  const location = useLocation();
  const { logout } = useAuth();
  const { appInfo } = useAppInfoContext();
  const navigate = useNavigate();
  const shareId = location.pathname.startsWith('/shared/') 
    ? location.pathname.substring('/shared/'.length)
    : paramShareId;
  const { colorMode, toggleColorMode } = useTheme();
  const toast = useToast();
  const [selectedConfig, setSelectedConfig] = useState<Config | null>(null);
  const [sharedCollection, setSharedCollection] = useState<Collection | null>(null);
  const [availableConfigs, setAvailableConfigs] = useState<Config[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingError, setStreamingError] = useState<string | null>(null);
  const [finalTime, setFinalTime] = useState<number | null>(null);
  const [gitlabSourcePath, setGitlabSourcePath] = useState<string | null>(null);
  const [isInputPanelCollapsed, setIsInputPanelCollapsed] = useState(true);
  const [currentInputs, setCurrentInputs] = useState<any>({});
  const [showChatInterface, setShowChatInterface] = useState(false);
  const inputAreaRef = useRef<InputAreaRef>(null);
  const responseViewerRef = useRef<ResponseViewerRef>(null);
  const assistantChatRef = useRef<AssistantChatClientRef>(null);
  const gitlabUploadCallbackRef = useRef<((variable: string, gitlabUrl: string, filePath: string) => Promise<void>) | null>(null);
  const { isOpen: isGitlabBrowserOpen, onOpen: openGitlabBrowser, onClose: closeGitlabBrowser } = useDisclosure();
  const [resolvedGitlabUrl, setResolvedGitlabUrl] = useState<string | null>(null);
  const [currentFileParameter, setCurrentFileParameter] = useState<string | null>(null);

  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const infoBg = useColorModeValue('blue.50', 'blue.900');
  const infoColor = useColorModeValue('blue.800', 'blue.200');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    if (!shareId) {
      setError('Invalid share link.');
      setIsLoading(false);
      return;
    }
    loadSharedDocument();
  }, [shareId]);

  const loadSharedDocument = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const encodedShareId = encodeURIComponent(shareId!);
      console.log('🔍 Loading shared content for shareId:', encodedShareId);
      
      // First, try to load as a shared collection
      const sharedCollectionResult = await CollectionService.getSharedCollection(encodedShareId);
      console.log('📦 Shared collection result:', sharedCollectionResult);
      
      if (sharedCollectionResult) {
        // This is a shared collection
        console.log('✅ Found shared collection:', sharedCollectionResult.name, 'with', sharedCollectionResult.documentIds.length, 'documents');
        setSharedCollection(sharedCollectionResult);
        
        // Load configs for documents in the collection
        console.log('📄 Loading all configs...');
        const allConfigs = await ConfigService.fetchConfigs();
        console.log('📄 All configs loaded:', allConfigs.length);
        
        const filteredConfigs = allConfigs.filter(config => 
          sharedCollectionResult.documentIds.includes(config.id)
        );
        console.log('🔍 Filtered configs:', filteredConfigs.length, 'out of', sharedCollectionResult.documentIds.length, 'document IDs');
        
        // Sort configs to match collection document order
        const orderedConfigs = sharedCollectionResult.documentIds
          .map(docId => filteredConfigs.find(config => config.id === docId))
          .filter(config => config !== undefined) as Config[];
        
        console.log('📋 Ordered configs:', orderedConfigs.length, orderedConfigs.map(c => c.name));
        setAvailableConfigs(orderedConfigs);
        
        // Set the first document as selected
        if (orderedConfigs.length > 0) {
          setSelectedConfig(orderedConfigs[0]);
          console.log('🎯 Selected first config:', orderedConfigs[0].name);
        }
      } else {
        console.log('📄 Not a shared collection, trying as shared document...');
        // This is a shared document
        const sharedConfig = await ConfigService.fetchSharedConfig(encodedShareId);
        console.log('📄 Found shared document:', sharedConfig?.name);
        setSelectedConfig(sharedConfig);
        setAvailableConfigs([sharedConfig]);
        setSharedCollection(null);
      }
    } catch (error: any) {
      console.error('❌ Error loading shared content:', error);
      if (error.response?.status === 404) {
        setError('Shared document or collection not found or has expired.');
      } else {
        setError('Failed to load shared content.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  const handleResponse = (content: string, streaming?: boolean) => {
    if (streaming) {
      // For streaming, append the content chunk to ResponseViewer
      responseViewerRef.current?.appendContent?.(content);
    } else {
      // For non-streaming, set the entire content
      responseViewerRef.current?.setContent?.(content);
    }
    setIsStreaming(streaming || false);
    if (!streaming) {
      setFinalTime(Date.now());
    }
  };

  const handleError = (error: string) => {
    setStreamingError(error);
    toast({
      title: 'Error',
      description: error,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };

  const handleStartRequest = () => {
    setIsStreaming(true);
    setStreamingError(null);
    setFinalTime(null);
    // Ensure complete reset before starting new streaming
    responseViewerRef.current?.reset?.();
    responseViewerRef.current?.startStreaming?.();
  };

  const handleComplete = () => {
    setIsStreaming(false);
    // Stop streaming in ResponseViewer
    responseViewerRef.current?.stopStreaming?.();
    if (finalTime) {
      setFinalTime(Math.floor((Date.now() - finalTime) / 1000));
    }
  };

  const handleStop = () => {
    setIsStreaming(false);
    // Stop streaming in ResponseViewer
    responseViewerRef.current?.stopStreaming?.();
    handleComplete();
  };

  // Pass this to InputArea so it can provide the upload callback
  const handleGitLabFileUploadRequest = (cb: (variable: string, gitlabUrl: string, filePath: string) => Promise<void>) => {
    gitlabUploadCallbackRef.current = cb;
  };

  // Called by InputArea when Browse Repository is clicked
  const handleToggleGitlabBrowser = async (paramVariable?: string) => {
    setCurrentFileParameter(paramVariable || null);
    // Resolve GitLab URL (config-specific or default)
    if (selectedConfig?.gitlabUrl) {
      setResolvedGitlabUrl(selectedConfig.gitlabUrl);
      openGitlabBrowser();
    } else {
      // Optionally show a toast or error if no repo is configured
      toast({
        title: 'GitLab Repository Not Configured',
        description: 'Please configure a GitLab repository for this document.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // When a file is selected from the repo browser
  const handleRepoFileSelect = (file: any) => {
    closeGitlabBrowser();
    if (gitlabUploadCallbackRef.current && resolvedGitlabUrl && file?.path) {
      gitlabUploadCallbackRef.current('', resolvedGitlabUrl, file.path);
    }
    setCurrentFileParameter(null);
  };

  // When a file is uploaded from the repo browser (upload icon)
  const handleRepoFileUpload = (file: any) => {
    closeGitlabBrowser();
    if (gitlabUploadCallbackRef.current && resolvedGitlabUrl && file?.path) {
      gitlabUploadCallbackRef.current('', resolvedGitlabUrl, file.path);
    }
    setCurrentFileParameter(null);
  };

  // Store previous config ID to detect actual changes
  const [previousConfigId, setPreviousConfigId] = useState<string | null>(null);
  // Store previous interface type to detect interface type changes
  const [previousShowChatInterface, setPreviousShowChatInterface] = useState<boolean>(false);
  
  // Reset when config changes
  useEffect(() => {
    // Only reset if we're actually changing to a different config
    if (selectedConfig?.id && previousConfigId && selectedConfig.id !== previousConfigId) {
      console.log('🔄 SharedDocument: Resetting for config change from', previousConfigId, 'to', selectedConfig.id);
      console.log('🔄 Interface types - Previous:', previousShowChatInterface, 'Current:', showChatInterface);
      
      // Reset ResponseViewer (always reset for non-chat document switching)
      if (responseViewerRef.current?.reset) {
        responseViewerRef.current.reset();
        console.log('✅ Reset ResponseViewer');
      }
      
      // Smart reset for AssistantChatClient - only reset when:
      // 1. Switching FROM chat TO non-chat interface, OR
      // 2. Switching FROM non-chat TO chat interface
      // But NOT when switching between chat documents (preserve session retention)
      const interfaceTypeChanged = previousShowChatInterface !== showChatInterface;
      
      if (assistantChatRef.current?.reset && interfaceTypeChanged) {
        assistantChatRef.current.reset();
        console.log('✅ Reset AssistantChatClient due to interface type change');
      } else if (assistantChatRef.current?.reset && !interfaceTypeChanged && showChatInterface) {
        console.log('🔄 Skipping AssistantChatClient reset - preserving chat session retention');
      }
      
      // Stop any processing in InputArea
      if (inputAreaRef.current?.stopProcessing) {
        inputAreaRef.current.stopProcessing();
      }
      
      // Clear form inputs
      setCurrentInputs({});
      
      // Reset streaming states
      setIsStreaming(false);
      setStreamingError(null);
      setFinalTime(null);
    }
    
    // Update UI based on application type
    const isChatApplication = selectedConfig?.requestUrl?.includes('chat-messages') || selectedConfig?.applicationType === 'chat';
    if (isChatApplication && selectedConfig) {
      setShowChatInterface(true);
      setIsInputPanelCollapsed(true);
    } else {
      setShowChatInterface(false);
      setIsInputPanelCollapsed(false);
    }
    
    // Remember the current config ID and interface type
    if (selectedConfig?.id) {
      setPreviousConfigId(selectedConfig.id);
    }
    setPreviousShowChatInterface(showChatInterface);
  }, [selectedConfig?.id, previousConfigId, showChatInterface]);

  if (isLoading && !selectedConfig) {
    return (
      <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="4xl" py={8}>
          <VStack spacing={8} align="center">
            <Spinner size="xl" color="primary.500" />
            <Text color={mutedColor}>Loading shared document...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="4xl" py={8}>
          <VStack spacing={8} align="center">
            <Alert status="error" borderRadius="md">
              <AlertIcon as={FaExclamationTriangle} />
              <VStack align="start" spacing={2}>
                <Heading size="md">Unable to Load Document</Heading>
                <Text>{error}</Text>
                <Button
                  onClick={loadSharedDocument}
                  colorScheme="primary"
                  size="sm"
                >
                  Try Again
                </Button>
              </VStack>
            </Alert>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (!selectedConfig && !isLoading && !error) {
    return (
      <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="4xl" py={8}>
          <VStack spacing={8} align="center">
            <Alert status="error" borderRadius="md">
              <AlertIcon as={FaExclamationTriangle} />
              <VStack align="start" spacing={2}>
                <Heading size="md">Unable to Load Document</Heading>
                <Text>No configuration found for this shared link.</Text>
                <Button
                  onClick={loadSharedDocument}
                  colorScheme="primary"
                  size="sm"
                >
                  Try Again
                </Button>
              </VStack>
            </Alert>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      {/* Header */}
      <Box
        as="header"
        bg={cardBg}
        borderBottom="1px"
        borderColor={borderColor}
        px={6}
        py={4}
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            {/* Logo */}
            <Box
              w={8}
              h={8}
              bg="primary.500"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaInfoCircle} color="white" />
            </Box>
            <Heading size="lg" color={textColor}>
              {appInfo.name}
            </Heading>
          </HStack>
          
          <HStack spacing={4}>
            {/* Theme Toggle */}
            <IconButton
              aria-label={colorMode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              icon={colorMode === 'dark' ? <FaSun /> : <FaMoon />}
              onClick={toggleColorMode}
              variant="ghost"
              size="sm"
            />
            
            {/* Logout Button */}
            <Button
              leftIcon={<FaSignOutAlt />}
              onClick={handleLogout}
              variant="outline"
              size="sm"
            >
              Logout
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Main content */}
      <Box flex="1" display="flex">
        {/* Left sidebar - Document Selector */}
        <Box 
          w="350px" 
          borderRight="1px" 
          borderColor={borderColor}
          bg={cardBg}
          p={4}
        >
          {/* Use the existing DocumentSelector component */}
          {(sharedCollection || selectedConfig) ? (
            <DocumentSelector
              onConfigSelect={(config) => setSelectedConfig(config)}
              selectedConfigId={selectedConfig?.id}
              sharedMode={true}
              sharedCollection={sharedCollection || undefined}
              sharedConfigs={availableConfigs}
            />
          ) : isLoading ? (
            <Text>Loading shared content...</Text>
          ) : (
            <Text>No shared content found.</Text>
          )}
        </Box>

        {/* Right side - Content container */}
        <Box flex="1" p={6}>
          <Box
            display="flex"
            flexDirection="column"
            flex="1"
            gap={6}
            minH="0"
            h="calc(100vh - 120px)"
          >
            {/* Top: Input Area */}
            <Box
              className="input-area-container"
              flex={isInputPanelCollapsed ? "0 0 auto" : "0 1 auto"}
              maxH={isInputPanelCollapsed ? "auto" : "50%"}
              minH="auto"
              h={isInputPanelCollapsed ? "auto" : "auto"}
              borderRadius="md"
              bg={cardBg}
              boxShadow="sm"
              border="1px"
              borderColor={borderColor}
              overflowY="auto"
              transition="all 0.3s ease"
            >
              <InputArea
                ref={inputAreaRef}
                config={selectedConfig}
                onResponse={handleResponse}
                onError={handleError}
                onStartRequest={handleStartRequest}
                onComplete={handleComplete}
                onGitLabFileUploadRequest={handleGitLabFileUploadRequest}
                onGitLabSourcePathUpdate={setGitlabSourcePath}
                onToggleGitlabBrowser={handleToggleGitlabBrowser}
                onInputChange={setCurrentInputs}
              />
            </Box>

            {/* Resize Handle - Only show when not collapsed and not chat application */}
            {!isInputPanelCollapsed && !(selectedConfig?.requestUrl?.includes('chat-messages') || selectedConfig?.applicationType === 'chat') && (
              <Box
                className="resize-handle"
                h="12px"
                cursor="row-resize"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={10}
              >
                <Box
                  className="resize-handle-bar"
                  w="48px"
                  h="4px"
                  borderRadius="sm"
                  bg="gray.300"
                  transition="background-color 0.2s ease"
                  _hover={{ bg: 'blue.500', opacity: 0.7 }}
                />
              </Box>
            )}

            {/* Docked Input Area Indicator */}
            {isInputPanelCollapsed && (
              <Box
                className="docked-indicator"
                h="2px"
                bg="blue.500"
                opacity={0.7}
                transition="opacity 0.2s ease"
              />
            )}

            {/* Bottom: Response Area */}
            <Box
              className="response-area-container"
              flex="1"
              borderRadius="md"
              bg={cardBg}
              boxShadow="sm"
              border="1px"
              borderColor={borderColor}
              overflow="hidden"
              minH="200px"
              h="100%"
              transition="all 0.3s ease"
            >
              {showChatInterface && selectedConfig ? (
                <AssistantChatClientRefactored
                  key="shared-chat-client"
                  ref={assistantChatRef}
                  config={selectedConfig}
                  initialInputs={currentInputs}
                  onError={handleError}
                  onClose={() => {
                    setShowChatInterface(false);
                    setIsInputPanelCollapsed(false);
                  }}
                  onExpandInputPanel={() => {
                    inputAreaRef.current?.togglePanel();
                  }}
                  isInputPanelCollapsed={isInputPanelCollapsed}
                />
              ) : (
                <ResponseViewer
                  key="shared-response-viewer"
                  ref={responseViewerRef}
                  config={selectedConfig}
                  onStop={handleStop}
                  gitlabSourcePath={gitlabSourcePath}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {/* GitLabFileBrowser Drawer (right sidebar) */}
      {isGitlabBrowserOpen && (
        <Box
          position="fixed"
          top={0}
          right={0}
          h="100vh"
          w="400px"
          zIndex={2000}
          bg={cardBg}
          boxShadow="-2px 0 8px rgba(0,0,0,0.08)"
          borderLeft="1px solid"
          borderColor={borderColor}
          display="flex"
          flexDirection="column"
        >
          <Box p={4} borderBottom="1px" borderColor={borderColor} fontWeight="bold">
            Browse Repository
            <Button onClick={closeGitlabBrowser} size="sm" float="right">Close</Button>
          </Box>
          <Box flex="1" overflowY="auto">
            <GitLabFileBrowser
              gitlabUrl={resolvedGitlabUrl || undefined}
              onFileSelect={handleRepoFileSelect}
              onFileUpload={handleRepoFileUpload}
              onClose={closeGitlabBrowser}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};